import { Link } from 'react-router-dom';
import { FolderOpen, LayoutGrid, QrCode, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import { seoConfig } from '../../configs/seoConfig';
import { getHoursSentence } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import './HowWeWorkPage.css';

const STEPS = [
  {
    icon: LayoutGrid,
    title: 'Choose a project',
    text: 'Browse the catalog and open a project to see what it includes, the tech stack and the price in INR.',
  },
  {
    icon: QrCode,
    title: 'Pay by UPI QR',
    text: 'At checkout, scan the QR code with any UPI app, then enter your UPI transaction ID.',
  },
  {
    icon: ShieldCheck,
    title: 'We verify your payment',
    text: 'Each payment is checked by hand. Orders are typically processed within 12 hours.',
  },
  {
    icon: FolderOpen,
    title: 'Open your project',
    text: 'Your order page gives you access: an invite to the GitHub repository and/or downloadable files.',
  },
];

// Only points that come straight from the Terms of Service.
const NOTES = [
  {
    title: 'Prices are in INR',
    text: 'Payment is made in full before delivery.',
  },
  {
    title: 'Typically within 12 hours',
    text: 'That is how long processing usually takes after a successful payment. At busy times it can take a little longer.',
  },
  {
    title: 'All sales are final',
    text: 'Digital products cannot be cancelled or refunded once purchased, so read the project details first.',
    link: { to: '/refund', label: 'Refund policy' },
  },
  {
    title: 'Ask before you buy',
    // A getter: the hours come from Admin → Settings.
    get text() {
      return `Not sure a project fits? Contact us first: ${getHoursSentence()}.`;
    },
    link: { to: '/contact', label: 'Contact us' },
  },
];

const HowWeWorkPage = () => {
  useSiteConfig();
  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.howWeWork} />
      <div className="pk-container">
        <PageHeader
          title="How it works"
          subtitle="From choosing a project to opening it on your order page, in four steps."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'How it works' }]}
          actions={
            <Link className="pk-btn pk-btn--primary" to="/products">
              Browse projects
            </Link>
          }
        />

        <section className="pk-section" aria-label="Steps">
          <ol className="hw-steps">
            {STEPS.map(({ icon: Icon, title, text }, index) => (
              <li key={title} className="pk-card hw-step">
                <div className="hw-step__top">
                  <span className="hw-step__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="hw-step__num" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h2 className="pk-title-3">
                  <span className="hw-sr">Step {index + 1}: </span>
                  {title}
                </h2>
                <p className="pk-muted">{text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="pk-section" aria-labelledby="hw-notes">
          <h2 id="hw-notes" className="pk-title-2">
            Good to know
          </h2>
          <dl className="hw-notes">
            {NOTES.map(note => (
              <div key={note.title} className="hw-note">
                <dt>{note.title}</dt>
                <dd>
                  {note.text}
                  {note.link && (
                    <>
                      {' '}
                      <Link className="pk-link" to={note.link.to}>
                        {note.link.label}
                      </Link>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <p className="pk-muted hw-terms">
            Full details are in the{' '}
            <Link className="pk-link" to="/terms">
              Terms of Service
            </Link>
            .
          </p>
        </section>
      </div>
    </main>
  );
};

export default HowWeWorkPage;
